export interface MediaGuidanceComponents {
    region: string;
    ethnicity: string;
    general: string;
}

/**
 * Parse the unified media_guidance field into component parts
 * (expecting format like "Region: <region>\nEthnicity: <ethnicity>\n<general guidance>")
 */
export const parseMediaGuidance = (guidance: string): MediaGuidanceComponents => {
    if (!guidance) return { region: '', ethnicity: '', general: '' };

    const lines = guidance.split('\n');
    let region = '';
    let ethnicity = '';
    let general = '';

    const regionPrefix = 'Region: ';
    const ethnicityPrefix = 'Ethnicity: ';

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.startsWith(regionPrefix)) {
            region = line.substring(regionPrefix.length).trim();
            if (region === 'any') region = '';
        } else if (line.startsWith(ethnicityPrefix)) {
            ethnicity = line.substring(ethnicityPrefix.length).trim();
            if (ethnicity === 'any') ethnicity = '';
        } else if (line.trim()) {
            // Everything else goes into general
            general += (general ? '\n' : '') + line;
        }
    }

    return { region, ethnicity, general };
};

/**
 * Whether a media guidance string carries any real preference, i.e. something
 * beyond the empty "Region: any / Ethnicity: any" scaffolding.
 */
export const hasMediaGuidance = (guidance: string | null | undefined): boolean => {
    const { region, ethnicity, general } = parseMediaGuidance(guidance ?? '');
    return !!(region || ethnicity || general);
};

/**
 * Format media guidance components into a unified string
 */
export const formatMediaGuidance = (region: string, ethnicity: string, general: string): string => {
    const parts = [];

    // Add region if provided (empty or 'any' means no preference)
    if (region && region.trim() && region !== 'any') {
        parts.push(`Region: ${region.trim()}`);
    } else {
        parts.push('Region: any');
    }

    // Add ethnicity if provided (empty or 'any' means no preference)
    if (ethnicity && ethnicity.trim() && ethnicity !== 'any') {
        parts.push(`Ethnicity: ${ethnicity.trim()}`);
    } else {
        parts.push('Ethnicity: any');
    }

    // Add general guidance if provided
    if (general && general.trim()) {
        parts.push(general.trim());
    }

    return parts.join('\n');
};
